import React, { useState, useRef, useEffect } from 'react';
import { useAuth, useLogout } from '../lib/api';
import { useNavigate, useLocation } from 'react-router-dom';
import type { PageName } from '../types';
import {
  DashboardIcon,
  ContentIcon,
  ChevronDownIcon,
  ArticlesPagesIcon,
  MultimediaIcon,
  AboutIcon,
  OpportunitiesIcon,
  EventsIcon,
  AnnouncementIcon,
  SubscribersIcon,
  AdminUsersIcon,
} from './icons';

interface SidebarProps {
  openArticleModal: () => void;
}

interface NavItemProps {
  icon?: React.ReactNode;
  label: PageName;
  route: string;
  isSubItem?: boolean;
}

interface AccordionItemProps {
  icon: React.ReactNode;
  label: PageName;
  children: React.ReactNode;
  isActive: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, route, isSubItem = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === route;

  return (
    <li
      className={`flex items-center cursor-pointer transition-colors duration-200 ${
        isSubItem
          ? `relative py-2 pl-12 pr-3 text-base ${
              isActive
                ? 'text-white font-medium'
                : 'text-white/80 hover:text-white'
            }`
          : `gap-3 px-5 py-2.5 rounded-md ${
              isActive
                ? 'bg-wine-200 text-white'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`
      }`}
      onClick={() => navigate(route)}
    >
      {isSubItem && (
        <div
          className={`absolute left-5 top-0 bottom-0 w-[1px] ${
            isActive ? 'bg-rose-100' : 'bg-white'
          }`}
        />
      )}
      {icon}
      <span className={isSubItem ? '' : 'text-sm font-medium'}>{label}</span>
    </li>
  );
};

const AccordionItem: React.FC<AccordionItemProps> = ({ icon, label, children, isActive }) => {
    const [isOpen, setIsOpen] = useState(isActive);

    useEffect(() => {
        if (isActive) {
            setIsOpen(true);
        }
    }, [isActive]);

    return (
        <li>
            <div className={`rounded-md ${isActive && isOpen ? 'bg-wine-200' : ''}`}>
                <div
                    className="flex items-center justify-between gap-3 px-5 py-2.5 cursor-pointer text-white/80 hover:text-white"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="flex items-center gap-3">
                        {icon}
                        <span className="text-base font-normal">{label}</span>
                    </div>
                    <ChevronDownIcon className={`w-5 h-5 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
                {isOpen && (
                    <ul className="pb-1">{children}</ul>
                )}
            </div>
        </li>
    );
};


const Sidebar: React.FC<SidebarProps> = ({  openArticleModal }) => {
  const [isCreateNewOpen, setCreateNewOpen] = useState(false);
  const createNewRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const logoutMutation = useLogout();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createNewRef.current && !createNewRef.current.contains(event.target as Node)) {
        setCreateNewOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [createNewRef]);
  
  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate('/login')
      },
      onError: () => {

        navigate('/login')
      }
    })
  }

  return (
    <aside className="w-64 bg-wine-100 text-white flex flex-col p-4 h-screen">
      <div className="text-3xl font-bold mb-8 px-2 font-pt-serif">AFSA</div>

      <div ref={createNewRef} className="relative mb-6">
        <button
          onClick={() => openArticleModal()}
          className="w-full flex justify-center items-center gap-2 bg-forestGreen-100 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-md transition-colors text-base h-10"
        >
          Create New
          <ChevronDownIcon className={`w-5 h-5 transition-transform mt-1 ${isCreateNewOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <nav className="flex-1 space-y-2 overflow-y-auto scrollbar-hide">
        <ul className="space-y-1">
            <NavItem
                icon={<DashboardIcon className="w-5 h-5" />}
                label="Dashboard"
                route="/admin/dashboard"
            />
            <NavItem
                icon={<ContentIcon className="w-5 h-5" />}
                label="Content"
                route="/admin/content"
            />
        </ul>
        
        <div className="pt-3">
            <h3 className="px-4 text-sm opacity-50 font-normal text-white  mb-2 space-x-0">Website Content</h3>
            <ul className="space-y-1">
                <AccordionItem
                    icon={<ArticlesPagesIcon className="w-5 h-5" />}
                    label="Articles & Pages"
                    isActive={['/admin/press-release', '/admin/strategic-pillars', '/admin/programmes'].includes(location.pathname)}
                >
                    <NavItem
                        label="Press Release"
                        route="/admin/press-release"
                        isSubItem
                    />
                     <NavItem
                        label="Strategic Pillars"
                        route="/admin/strategic-pillars"
                        isSubItem
                    />
                     <NavItem
                        label="Programmes"
                        route="/admin/programmes"
                        isSubItem
                    />
                </AccordionItem>
                <AccordionItem icon={<ContentIcon className="w-5 h-5" />} label="Resources" isActive={location.pathname === '/admin/publications'}>
                     <NavItem
                        label="Publications"
                        route="/admin/publications"
                        isSubItem
                    />
                </AccordionItem>
                <AccordionItem icon={<MultimediaIcon className="w-5 h-5" />} label="Multimedia" isActive={['/admin/photo-gallery', '/admin/videos'].includes(location.pathname)}>
                    <NavItem
                        label="Photo Gallery"
                        route="/admin/photo-gallery"
                        isSubItem
                    />
                    <NavItem
                        label="Videos"
                        route="/admin/videos"
                        isSubItem
                    />
                </AccordionItem>
                 <AccordionItem icon={<AboutIcon className="w-5 h-5" />} label="About" isActive={location.pathname === '/admin/about'}>
                    {null}
                </AccordionItem>
                 <AccordionItem icon={<OpportunitiesIcon className="w-5 h-5" />} label="Opportunities" isActive={location.pathname === '/admin/opportunities'}>
                    {null}
                </AccordionItem>
                 <NavItem
                    icon={<EventsIcon className="w-5 h-5" />}
                    label="Events"
                    route="/admin/events"
                />
                 <NavItem
                    icon={<AnnouncementIcon className="w-5 h-5" />}
                    label="Announcement"
                    route="/admin/announcement"
                />
            </ul>
        </div>

        <div className="pt-3">
            <h3 className="px-4 text-sm opacity-50 font-normal text-white  mb-2 space-x-0">Others</h3>
            <ul className="space-y-1">
                 <NavItem
                    icon={<SubscribersIcon className="w-5 h-5" />}
                    label="Subscribers"
                    route="/admin/subscribers"
                />
                <NavItem
                    icon={<AdminUsersIcon className="w-5 h-5" />}
                    label="Admin Users"
                    route="/admin/admin-users"
                />
            </ul>
        </div>
      </nav>

      <div className="mt-auto">
        <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/10 cursor-pointer">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Account</p>
            <p className="text-xs text-gray-300 truncate">{user?.email || 'test@gmail.com'}</p>
          </div>
          <button
            onClick={handleLogout}
            disabled={logoutMutation.isPending}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Sign out"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;